import {
  configOption,
  configSubObject,
  KahunConfigurationOptionType,
} from "../lib/annotations";
import { DiagnosisOptions, SortType } from "./diagnosis";

export class SummaryOptions {
  @configOption({
    description: "Discard 'No' prior to negated findings",
    displayName: "Discard 'No' prior to negated findings",
  })
  discardNoForNegate?: boolean = false;

  @configOption({
    description: "Show in inches, false for centimeters",
  })
  heightInInches?: boolean = true;

  @configOption({ description: "Show assessment in the summary" })
  showAssessment?: boolean = true;

  @configOption({
    description: "Show in pounds, false for kilograms",
  })
  weightInPounds?: boolean = true;

  @configOption({
    description:
        "Add Icd code to API and summary",
  })
  showIcdCodes?: boolean = false;

  @configOption({
    description:
        "Summary of the patient exchanges",
  })
  conversationSummary?: boolean = false;
  @configOption({
    description:
      "If marked, in cases with no background information a message stating there there is no background information, or medication provided will appear at the summary",
  })
  showNoBackgroundText?: boolean = false;
}

export class ConversationOptions {
  @configOption({
    description:
      "Used for the runner, states if the conversation should stop when reaching the max number of questions or entropy limit",
  })
  shouldStop?: boolean = true;

  @configOption({
    description: "The entropy/urgency threshold for an early end of the conversation (-1 = off).",
    displayType: KahunConfigurationOptionType.Float,
  })
  promptMinimumInformation?: number = -1;

  @configOption({
    description: "When reaching below this value stop the conversation",
    displayType: KahunConfigurationOptionType.Float,
  })
  minimumInformation?: number = 0.13;

  @configOption({
    description: "don't ask refinement with entropy below this",
    displayType: KahunConfigurationOptionType.Float,
  })
  minRefinementInformation?: number = 0.06;

  @configOption({
    description: "Don't ask background questions with entropy below this",
    displayType: KahunConfigurationOptionType.Float,
  })
  minBackgroundInformation?: number = 0.04;

  @configOption({
    description:
      "In synergic the maximum number of suggestions we group together",
    displayName: "Synergic count",
    displayType: KahunConfigurationOptionType.Int,
  })
  tripleQuestionCount?: number = 3;

  @configSubObject({})
  summaryOptions?: SummaryOptions = {};

  @configOption({
    description:
      "Make patinet1st ask for the numerical value once reporting a relevant symptom. For example, if patient reports Anemia, Kahun will ask for the Hemoglobin level",
  })
  numericalQualification?: boolean = true;

  @configOption({
    description:
      "Make patient1st ask for severity level in 0-10 scale (using a diagram) instead of just the usual mild, moderate, severe",
  })
  usePainScaleOf10?: boolean = false;

  @configOption({
    displayType: KahunConfigurationOptionType.WPolicy,
    description: "List of policies for the conversation to use",
  })
  policies?: string[] = [];

  @configOption({
    description: "List of Protocol IDs to disable from running",
  })
  excludedProtocols?: string[] = [];

  @configOption({
    description: "True when we want to use policies marked as default",
  })
  useDefaultPolicies?: boolean = true;

  @configOption({
    description: "Show temperature questions in Fahrenheit, false for Celsius",
  })
  temperatureInFahrenheit?: boolean = false;

  @configOption({
    description:
      "True when we want to include the citations in the conversationDD, this is hard-coded true for the demo mode",
  })
  includeDiseaseCitations?: boolean = false;

  @configOption({
    description: "Entropy smooth factor for the plan",
    displayType: KahunConfigurationOptionType.Float,
  })
  planExpectedEntropySmoothFactor?: number = 0.0;

  @configOption({
    description: "Maximum number of next suggestions",
    displayType: KahunConfigurationOptionType.Int,
  })
  nextSuggestionsListLimit?: number = 100;

  @configOption({
    description:
        "Use only top X diseases from each cluster for next step suggestions.",
    displayType: KahunConfigurationOptionType.Int,
  })
  useTopDiseasesFromEachCluster?: number = 3; // use only top X diseases from each cluster for next step suggestions.

  @configOption({
    description:
        "use only top X diseases for next step suggestions.",
    displayType: KahunConfigurationOptionType.Int,
  })
  limitDiseasesForNextStep?: number = 20; // use only top X diseases for next step suggestions.

  @configOption({
    description: "Complete the chat after this number of questions",
  })
  maxNumOfQuestions?: number = 35;

  @configOption({
    description:
      "When this flag is true always return next suggestions. Usefull for demo mode and debugging.",
  })
  forceReasoningComputation?: boolean = false;

  @configOption({
    displayName: "WebHook enabled",
    description: "Change to true to enable webhook notifications",
  })
  notifyStatusChanges?: boolean = false;

  @configOption({
    options: SortType,
    description: "SortType for next question DD",
  })
  sortTypeForNextStep?: SortType = SortType.Urgency;

  @configOption({
    options: SortType,
    description: "SortType for next results DD",
  })
  sortTypeForResults?: SortType = SortType.PatientProb;

  @configOption({
    displayType: KahunConfigurationOptionType.WPanel,
    description: "Panel to use for the medical history question",
  })
  medicalHistoryPanelId?: string = "FE.d49c3964-3e5e-452d-b409-dfb12b2c3a7c";

  @configOption({ displayType: KahunConfigurationOptionType.WPanel,
    description: "Panel to use for the family history panel" })
  familyHistoryPanelId?: string = null;

  @configOption({ description: "Show medication question" })
  askMedication?: boolean = false;

  @configOption({ description: "Show allergies question" })
  askAllergies?: boolean = false;

  @configOption({
    description: "Show free text question at the end of the conversation",
  })
  askFreeText?: boolean = false;

  @configOption({
    description:
      "List of recommendations which will cause the chat to end when they are set by any protocol. Can also provide parent of a group of recommendations",
  })
  endChatOnRecommendation?: string[];

  @configOption({
    displayType: KahunConfigurationOptionType.WFeature,
    description:
      "List of values for the Custom Patient Facing property. When this is set, features with matching properties values will be treated as patientFacing",
    customParams: {
      searchOptions: {
        patientFacing: false,
      }
    }
  })
  customPatientFacingPropertyValues?: string[] = [];

  @configSubObject({
    description: "options that control the DD used to generate suggestions",
  })
  nextStepDD?: DiagnosisOptions;

  @configOption({
    description:
      "In protocols, group up to this number of questions(synergic if exists)",
  })
  protocolsQuestionGroupCount?: number = 8;

  @configOption({
    description:
       "In protocols, group questions by rule first during synergic",
  })
    groupQuestionsByRule?: boolean = true;

  @configSubObject({
    description: "options that control the DD used to generate final results",
  })
  resultsDD?: DiagnosisOptions;

  @configOption({
    description:
      "Suspected localized suggestions (such as swelling of right wrist joint) ",
  })
  localizedSuggestions?: boolean = true;

  @configOption({
    description: "Do not warn user of incomplete information at end of chat",
  })
  skipIncompleteInformationQuestion?: boolean = false;

  @configOption({
    description:
        "Convert the duration question to numeric",
  })
  numericDuration?: boolean = true;

  @configOption({
    displayType: KahunConfigurationOptionType.WPanel,
    description:
        "A panel consists of visit types options",
  })
  reasonForVisitPanel?: string = '';

  @configOption({
    description:
      "Send a followup form question after answering yes to pregnancy?",
  })
  sendPregnancyFollowUpForm?: boolean = false;

  @configOption({
    description:
        "Enable criteria protocols. This will generate questions and implied findings based on criteria protocols",
  })
  enableCriteria?: boolean = false;

  @configOption({
    description:
        "For next step suggestions use MI that differentiate between urgencies rather than disease"
  })
  useMIforUrgencyDifferentiation?: boolean = false;


  @configOption({
    description:
        "Use protocols and critieria which are in testing (pre-release) stage"
  })
  enableProtocolsInTestingStage?: boolean = false;

  @configOption({
    displayType: KahunConfigurationOptionType.Boolean,
    displayName: "Multiple Complaints",
    description:
        "When turned on, instead of chief complaint we will show a dialog which will ask for any feature its duraion and will allow adding multiple symptoms at once",
  })
  multipleComplaints?: boolean = false;

  @configOption({
    displayType: KahunConfigurationOptionType.Boolean,
    displayName: "Ignore EHR findings",
    description:
        "When turned on, CSL will use a patient case without the EHR/API findings",
  })
  ignoreEhrFindings?: boolean = false;

  @configOption({
    displayType: KahunConfigurationOptionType.Boolean,
    displayName: "Ignore vital_signs findings",
    description:
        "When turned on, CSL will use a patient case without the vital signs findings",
  })
  ignoreVitalSignsFindings?: boolean = false;

  nextStepCategories?: string[] = null;

}
